import { useState, useCallback, useEffect } from 'react';
import { FormData, ValidationError } from '../types';
import { validateForm, validateSingleField } from '../utils/validation';
import { createSession, Session } from '../utils/session';

const INITIAL_FORM_DATA: FormData = {
  tester_name: '',
  // Section 1
  full_name: '',
  age: 0,
  weight_kg: 0,
  city: '',
  occupation: '',
  relationship_status: '',
  current_sexual_activity: '',
  first_consultation: '',
  previous_treatments: [],
  emergency_red_flags: '',
  // Section 2
  main_issue: '',
  // ED fields
  ed_duration: undefined,
  ed_gets_erections: undefined,
  ed_partner_arousal_speed: undefined,
  ed_partner_maintenance: undefined,
  ed_partner_hardness: undefined,
  ed_morning_erections: undefined,
  ed_masturbation_imagination: undefined,
  // PE fields
  pe_duration: undefined,
  pe_partner_ejaculation_time: undefined,
  pe_partner_penile_sensitivity: undefined,
  pe_partner_masturbation_control: undefined,
  // Section 3
  masturbation_method: '',
  masturbation_grip: undefined,
  masturbation_frequency: undefined,
  porn_frequency: '',
  performance_anxiety: '',
  multiple_partners: undefined,
  issues_all_partners: undefined,
  partner_response: undefined,
  // Section 4
  medical_conditions: [],
  medical_conditions_other: undefined,
  current_medications: [],
  current_medications_other: undefined,
  spinal_genital_surgery: '',
  alcohol_frequency: '',
  smoking_frequency: '',
  substance_consumption: undefined,
  stress_level: '',
  sleep_quality: '',
  physical_activity: '',
  // Section 5
  additional_info: undefined,
};

export function useForm() {
  const [formData, setFormData] = useState<FormData>(() => {
    // Load from localStorage if available
    const saved = localStorage.getItem('rxmen-form-data');
    return saved ? JSON.parse(saved) : INITIAL_FORM_DATA;
  });

  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [session] = useState<Session>(createSession);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-save to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem('rxmen-form-data', JSON.stringify(formData));
    }, 500);
    return () => clearTimeout(timer);
  }, [formData]);

  const updateField = useCallback((field: keyof FormData, value: any) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value,
      };
      // Clear error for this field when value changes
      setErrors(prevErrors => prevErrors.filter(e => e.field !== field));
      return updated;
    });
  }, []);

  // Real-time validation for a single field
  const validateField = useCallback((fieldName: keyof FormData, value: any, isRequired: boolean = false): string | null => {
    const error = validateSingleField(fieldName, value, formData, isRequired);
    if (error) {
      setErrors(prev => {
        const filtered = prev.filter(e => e.field !== fieldName);
        return [...filtered, { field: String(fieldName), message: error }];
      });
      return error;
    } else {
      setErrors(prev => prev.filter(e => e.field !== fieldName));
      return null;
    }
  }, [formData]);

  const validate = useCallback((): boolean => {
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);
    return validationErrors.length === 0;
  }, [formData]);

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setErrors([]);
    localStorage.removeItem('rxmen-form-data');
  }, []);

  return {
    formData,
    errors,
    session,
    isSubmitting,
    setIsSubmitting,
    updateField,
    validate,
    validateField,
    resetForm,
    setFormData,
  };
}

